type Options = Partial<PlusMinusOptions>;

export interface PlusMinusElement extends HTMLElement {
  plusminus?: PlusMinus;
}

export interface PlusMinusOptions {
  actionEl: string;
  actionData: string;
  changedClass: string;
  fieldEl: string;
  maximumCartLimit: string;
  maxValue: number;
  minValue: number;
  selector: string;
}

export class PlusMinus {
  static defaults = {
    actionData: 'action',
    actionEl: '[data-plusminus-action]',
    changedClass: 'has-changed',
    fieldEl: '[data-plusminus-field]',
    maxValue: 10,
    maximumCartLimit:
      "You've reached the maximum number of products per order.",
    minValue: 1,
    selector: '[data-plusminus]',
  };
  state = {
    initialValue: 1,
  };
  element: PlusMinusElement;
  field: HTMLInputElement;
  minValue: number;
  maxValue: number;
  options: PlusMinusOptions;
  clickHandler: EventListener;
  fieldHandler: EventListener;
  keyPressHandler: EventListener;

  constructor(element: PlusMinusElement, options: Options = {}) {
    if (!element || element.plusminus) {
      return;
    }

    this.element = element;
    this.options = {
      ...PlusMinus.defaults,
      ...this.element.dataset,
      ...options,
    };

    const field = this.element.querySelector(
      this.options.fieldEl,
    ) as HTMLInputElement;

    if (!field) {
      return;
    }

    this.field = field;

    const min = this.field.getAttribute('min') || String(this.options.minValue);
    const max = this.field.getAttribute('max') || String(this.options.maxValue);
    this.minValue = parseInt(min, 10);
    this.maxValue = parseInt(max, 10);
    this.state.initialValue = parseInt(this.field.value, 10);

    this.bind();
  }

  bind() {
    this.clickHandler = this.handleClick.bind(this);
    this.fieldHandler = this.handleFieldChange.bind(this);
    this.keyPressHandler = this.handleKeyPress.bind(this);

    this.element.addEventListener('click', this.clickHandler);
    this.element.addEventListener('change', this.fieldHandler);

    const match = this.element.querySelector(this.options.fieldEl);
    if (!match) {
      return;
    }
    match.addEventListener('keypress', this.keyPressHandler);
  }

  handleKeyPress(event: Event) {
    const target = event.target as HTMLInputElement;

    if (!target) {
      return;
    }

    const value = parseInt(target.value, 10);

    if (
      value > this.options.maxValue &&
      ((event as KeyboardEvent).key === 'Enter' ||
        (event as KeyboardEvent).which === 13)
    ) {
      event.stopPropagation();
      event.preventDefault();
      this.sendErrorMessage();
    }
  }

  handleClick(event: Event) {
    const target = event.target as HTMLElement;
    const match = target.closest(this.options.actionEl) as HTMLElement;

    if (!match) {
      return;
    }

    const action = match.dataset[this.options.actionData];
    if (action === 'minus') {
      this.update(-1);
      return;
    }

    this.update(+1);
  }

  handleFieldChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const match = target.closest(this.options.fieldEl);

    if (!match) {
      return;
    }

    const val = parseInt(target.value, 10);

    val > this.options.maxValue ? this.sendErrorMessage() : this.decorate(val);
  }

  update(op: number) {
    const val = this.getNewValue(op);

    this.field.value = String(val);

    this.decorate(val);
  }

  getNewValue(operator: number) {
    const val = parseInt(this.field.value, 10);

    if (val + operator <= this.minValue || isNaN(val)) {
      return this.minValue;
    } else if (val + operator > this.maxValue) {
      this.sendErrorMessage();
      return this.maxValue;
    } else {
      return val + operator;
    }
  }

  decorate(val: number) {
    this.element.classList.toggle(
      this.options.changedClass,
      val !== this.state.initialValue,
    );
  }

  sendErrorMessage() {
    const messageEvent = new CustomEvent('message:updated', {
      bubbles: true,
      detail: {
        text: this.options.maximumCartLimit,
        type: 'limit',
      },
    });
    this.element.dispatchEvent(messageEvent);
  }

  destroy() {
    this.element.removeEventListener('click', this.clickHandler);
    this.element.removeEventListener('change', this.fieldHandler);
    this.element.removeEventListener('keypress', this.fieldHandler);
  }
}
